import React, { useState, useEffect, useCallback } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const PortfolioSection = () => {
  const { language } = useLanguage();
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const touchStart = React.useRef(null);

  const onTouchStart = (e) => {
    touchStart.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
  };
  const onTouchEnd = (e) => {
    if (!touchStart.current) return;
    const dx = e.changedTouches[0].clientX - touchStart.current.x;
    const dy = e.changedTouches[0].clientY - touchStart.current.y;
    touchStart.current = null;
    if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy)) {
      dx < 0 ? showNext() : showPrev();
    }
  };

  const content = {
    es: {
      title: 'Nuestros Proyectos',
      subtitle: 'Tu espacio es una expresión de tu identidad y estilo de vida; acompañado por una visión que lo convierte en un lugar de significado.'
    },
    en: {
      title: 'Our Projects',
      subtitle: 'Your space is an expression of your identity and lifestyle; accompanied by a vision that transforms it into a place of meaning.'
    }
  };

  const text = content[language];

  const projects = [
    { image: '/images/portfolio/carlota-mueble-tv.webp', title: 'Habitación Carlota - Mueble TV' },
    { image: '/images/portfolio/mariano-majo-sala-lounge.webp', title: 'Mariano y Majo - Sala Lounge' },
    { image: '/images/portfolio/mariano-majo-habitacion.webp', title: 'Mariano y Majo - Habitación' },
    { image: '/images/portfolio/mariano-majo-cabecera.webp', title: 'Mariano y Majo - Cabecera Cama' },
    { image: '/images/portfolio/principal-muro-tv.webp', title: 'Habitación Principal - Muro TV' },
    { image: '/images/portfolio/carlota.webp', title: 'Habitación Carlota' },
    { image: '/images/portfolio/pto-estudio-sala.webp', title: 'Puerto Aventuras - Estudio y Sala' },
    { image: '/images/portfolio/pto-habitacion.webp', title: 'Puerto Aventuras - Habitación' },
    { image: '/images/portfolio/pto-escalera.webp', title: 'Puerto Aventuras - Escalera' },
    { image: '/images/portfolio/pto-comedor.webp', title: 'Puerto Aventuras - Comedor' },
    { image: '/images/portfolio/pto-sala.webp', title: 'Puerto Aventuras - Sala' },
    { image: '/images/portfolio/recibidor.webp', title: 'Recibidor' }
  ];

  const closeLightbox = useCallback(() => setLightboxIndex(null), []);
  const showPrev = useCallback((e) => {
    if (e) e.stopPropagation();
    setLightboxIndex((i) => (i - 1 + projects.length) % projects.length);
  }, [projects.length]);
  const showNext = useCallback((e) => {
    if (e) e.stopPropagation();
    setLightboxIndex((i) => (i + 1) % projects.length);
  }, [projects.length]);

  useEffect(() => {
    if (lightboxIndex === null) return;
    const onKey = (e) => {
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowLeft') showPrev();
      if (e.key === 'ArrowRight') showNext();
    };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [lightboxIndex, closeLightbox, showPrev, showNext]);

  return (
    <section id="portfolio" className="py-32 bg-neutral-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-20">
          <p className="text-amber-600 text-sm font-light tracking-[0.3em] uppercase mb-4">Portfolio</p>
          <h2 className="text-5xl md:text-6xl font-light text-neutral-900 mb-6 tracking-tight">
            {text.title}
          </h2>
          <p className="text-lg text-neutral-600 font-light max-w-3xl mx-auto leading-relaxed">{text.subtitle}</p>
        </div>

        {/* Gallery */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {projects.map((project, index) => (
            <div
              key={index}
              data-testid={`portfolio-item-${index}`}
              onClick={() => setLightboxIndex(index)}
              className="group relative overflow-hidden rounded-sm shadow-lg hover:shadow-2xl transition-all duration-500 cursor-pointer"
            >
              <div className="aspect-[3/2] overflow-hidden">
                <img
                  src={project.image}
                  alt={project.title}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-neutral-900/0 group-hover:bg-neutral-900/40 transition-all duration-500 flex items-end justify-center pb-8">
                  <p className="text-white text-sm font-light tracking-wide opacity-0 group-hover:opacity-100 transition-opacity duration-500 px-4 text-center">
                    {project.title}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Lightbox */}
      {lightboxIndex !== null && (
        <div
          data-testid="lightbox-overlay"
          onClick={closeLightbox}
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
          className="fixed inset-0 z-[10000] bg-neutral-950/95 backdrop-blur-sm flex items-center justify-center animate-in fade-in duration-300"
        >
          <button
            data-testid="lightbox-close-button"
            onClick={closeLightbox}
            aria-label="Cerrar"
            className="absolute top-6 right-6 text-white/70 hover:text-white transition-colors duration-300 z-10"
          >
            <X size={32} strokeWidth={1} />
          </button>

          <button
            data-testid="lightbox-prev-button"
            onClick={showPrev}
            aria-label="Anterior"
            className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 text-white/60 hover:text-white transition-colors duration-300 z-10 p-2"
          >
            <ChevronLeft size={44} strokeWidth={1} />
          </button>

          <button
            data-testid="lightbox-next-button"
            onClick={showNext}
            aria-label="Siguiente"
            className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 text-white/60 hover:text-white transition-colors duration-300 z-10 p-2"
          >
            <ChevronRight size={44} strokeWidth={1} />
          </button>

          <figure
            className="max-w-[88vw] max-h-[88vh] flex flex-col items-center"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              data-testid="lightbox-image"
              src={projects[lightboxIndex].image}
              alt={projects[lightboxIndex].title}
              className="max-w-full max-h-[78vh] object-contain shadow-2xl"
            />
            <figcaption className="mt-5 text-center">
              <p data-testid="lightbox-title" className="text-white text-sm font-light tracking-[0.2em] uppercase">
                {projects[lightboxIndex].title}
              </p>
              <p data-testid="lightbox-counter" className="text-white/40 text-xs font-light mt-1 tracking-widest">
                {lightboxIndex + 1} / {projects.length}
              </p>
            </figcaption>
          </figure>
        </div>
      )}
    </section>
  );
};

export default PortfolioSection;